import type { ApiPlan } from '../api';

export type BillingTerm = 'monthly' | 'halfyearly' | 'yearly';

export type PlanPricingOption = {
  term: BillingTerm;
  label: string;
  months: number;
  priceInr: number;
  pricePaise?: number;
};

const TERM_MONTHS: Record<BillingTerm, number> = {
  monthly: 1,
  halfyearly: 6,
  yearly: 12,
};

export const TERM_LABELS: Record<BillingTerm, string> = {
  monthly: '1 month',
  halfyearly: '6 months',
  yearly: '1 year',
};

const BILLING_TERMS: BillingTerm[] = ['monthly', 'halfyearly', 'yearly'];

/** Map legacy quarterly URLs / stored values to halfyearly. */
export function normalizeBillingTerm(term: string | null | undefined): BillingTerm {
  if (term === 'quarterly') return 'halfyearly';
  if (term === 'monthly' || term === 'halfyearly' || term === 'yearly') return term;
  return 'monthly';
}

export function isBillingTerm(value: string | null | undefined): value is BillingTerm {
  return value === 'monthly' || value === 'halfyearly' || value === 'yearly' || value === 'quarterly';
}

export function getPricingOptions(plan: ApiPlan | null | undefined): PlanPricingOption[] {
  if (!plan) return [];
  if (plan.pricingOptions?.length) {
    return plan.pricingOptions
      .map((o) => ({ ...o, term: normalizeBillingTerm(o.term) }))
      .filter((o) => BILLING_TERMS.includes(o.term)) as PlanPricingOption[];
  }
  if (plan.pricing) {
    return BILLING_TERMS.filter((t) => {
      const row = plan.pricing?.[t] ?? (t === 'halfyearly' ? plan.pricing?.quarterly : undefined);
      return row?.enabled && (row?.priceInr ?? 0) > 0;
    }).map((term) => ({
      term,
      label: TERM_LABELS[term],
      months: TERM_MONTHS[term],
      priceInr: (plan.pricing![term] ?? plan.pricing!.quarterly)!.priceInr,
    }));
  }
  if (plan.priceInr > 0) {
    const term = (plan.billingPeriod === 'yearly' ? 'yearly' : 'monthly') as BillingTerm;
    return [{ term, label: TERM_LABELS[term], months: TERM_MONTHS[term], priceInr: plan.priceInr }];
  }
  return [];
}

export function hasValidPlanPricing(pricing?: ApiPlan['pricing']): boolean {
  if (!pricing) return false;
  return BILLING_TERMS.some((t) => {
    const row = pricing[t] ?? (t === 'halfyearly' ? (pricing as { quarterly?: { priceInr?: number; enabled?: boolean } }).quarterly : undefined);
    return row?.enabled && (row?.priceInr ?? 0) > 0;
  });
}

export function defaultBillingTerm(plan: ApiPlan | null | undefined): BillingTerm {
  if (!plan) return 'monthly';
  if (plan.defaultBillingTerm) return normalizeBillingTerm(plan.defaultBillingTerm);
  return getPricingOptions(plan)[0]?.term || 'monthly';
}

export function resolvePlanPrice(plan: ApiPlan | null | undefined, term: BillingTerm): number | null {
  const normalized = normalizeBillingTerm(term);
  const opt = getPricingOptions(plan).find((o) => o.term === normalized);
  return opt ? opt.priceInr : null;
}

export function formatPlanPrice(inr: number): string {
  return `₹${inr.toLocaleString('en-IN')}`;
}

/** Effective monthly rate for multi-month terms (rounded). */
export function perMonthEquivalent(priceInr: number, months: number): number {
  if (months <= 1) return priceInr;
  return Math.round(priceInr / months);
}

/** Savings % vs paying the monthly tier for the same number of months. */
export function termSavingsPercent(monthlyPrice: number, termPrice: number, months: number): number | null {
  if (months <= 1 || monthlyPrice <= 0) return null;
  const fullPrice = monthlyPrice * months;
  if (fullPrice <= termPrice) return null;
  return Math.round(((fullPrice - termPrice) / fullPrice) * 100);
}

export function lowestOptionPrice(plan: ApiPlan | null | undefined): PlanPricingOption | null {
  const opts = getPricingOptions(plan);
  if (!opts.length) return null;
  return opts.reduce((a, b) => (a.priceInr <= b.priceInr ? a : b));
}

export function emptyPlanPricing() {
  return {
    monthly: { priceInr: 0, enabled: true },
    halfyearly: { priceInr: 0, enabled: false },
    yearly: { priceInr: 0, enabled: false },
  };
}
